/**
 * UI Store
 *
 * Manages global UI state that doesn't belong to server data.
 * Uses Zustand for lightweight, performant state management.
 */

import { create } from 'zustand';

/**
 * UI state interface
 */
interface UIState {
  /** Whether the sidebar/navigation drawer is open (mobile) */
  sidebarOpen: boolean;

  /**
   * One-slot handoff for {@link UIActions.showToast}. Nothing renders this field directly:
   * `UiStoreToastBridge` (mounted inside `ToastProvider` in App.tsx) forwards each write to the
   * context toast system and clears the slot again. Before that bridge existed (2026-09-22) every
   * `showToast` call in the app was silently dropped.
   */
  toast: ToastState | null;

  /** Whether the app is in offline mode */
  isOffline: boolean;
}

/**
 * Toast notification state (the shape every `showToast` caller passes).
 * `duration` is in milliseconds; when omitted the toast system's per-variant default applies.
 */
export interface ToastState {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  /** Optional dedupe key: a toast with the same id replaces the visible one instead of stacking. */
  id?: string;
}

/**
 * UI actions interface
 */
interface UIActions {
  /** Toggle sidebar open/closed state */
  toggleSidebar: () => void;

  /** Set sidebar open state explicitly */
  setSidebarOpen: (open: boolean) => void;

  /** Show a toast notification */
  showToast: (toast: ToastState) => void;

  /** Clear the current toast */
  clearToast: () => void;

  /** Set offline status */
  setOffline: (offline: boolean) => void;
}

/**
 * Combined UI store type
 */
type UIStore = UIState & UIActions;

/**
 * UI store hook
 *
 * Usage:
 * ```tsx
 * const sidebarOpen = useUIStore((state) => state.sidebarOpen);
 * const toggleSidebar = useUIStore((state) => state.toggleSidebar);
 * ```
 */
export const useUIStore = create<UIStore>((set) => ({
  // Initial state
  sidebarOpen: false,
  toast: null,
  isOffline: !navigator.onLine,

  // Actions
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  showToast: (toast) => set({ toast }),

  clearToast: () => set({ toast: null }),

  setOffline: (offline) => set({ isOffline: offline }),
}));

/**
 * Initialize offline detection
 *
 * Call this once at app startup to sync offline state with browser.
 */
export function initOfflineDetection(): () => void {
  const handleOnline = () => useUIStore.getState().setOffline(false);
  const handleOffline = () => useUIStore.getState().setOffline(true);

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}
